
import AppSelectTier from "@/components/company/SelectTier.vue";
import {Company} from "@/models/company";
import { CompanyDidNotPurchaseReasons, DidNotPurchaseReason } from "@/models/company-did-not-purchase-reason";
import { DiscoveredUsFrom } from "@/models/discovered-us-from";
import { PaymentFrequency } from "@/models/plan";
import { CompanyService } from "@/services/company-service";
import { Tier } from "@/utils/tier";
import { setTitle } from "@/utils/title";
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import AppSelectDiscoveredUsFrom from "./SelectDiscoveredUsFrom.vue";
import AppSelectCompanyDidNotPurchaseReason from "./SelectCompanyDidNotPurchaseReason.vue";
import AppCompanyEditVars from "./CompanyEditVars.vue";
import { AuthService, MainSocketService, MetricService } from "@/services";
import { DbInfo } from "@/models/db-info";

export default Vue.extend({
	data(){
		return {
			discoveredUsFrom: new DiscoveredUsFrom(),
			oldTier: Tier.Starter,
			oldDemoStatus: false,
			didNotPurchaseReasons: [] as DidNotPurchaseReason[],
			didNotPurchaseReasonExplained: "",
			isBeingMoved: false,
			availableDbs: [] as DbInfo[]
		};
	},
	props: {
		value: {
			type: Object as () => Company,
			required: true
		},
		isInfo: Boolean
	},
	computed: {
		moveDbItems():ISelectItem<Function>[] {
			return this.availableDbs.map(v=>{
				return {
					text: `Naar db` + v.toString(),
					value: ()=>this.moveToDb(v.id)
				};
			});
		},
		moveDbItemsQueued():ISelectItem<Function>[] {
			return this.availableDbs.map(v=>{
				return {
					text: `Naar db` + v.toString(),
					value: ()=>this.moveToDbQueued(v.id)
				};
			});
		},
		permanentlyActive(): boolean {
			return this.value.activeUntil.getTime() == 0;
		},
		permanentServicePack(): boolean {
			if (!this.value.servicePackExpirationDate) {
				return true;
			}
			return false;
		},
		discoveredUsFromChanged():boolean {
			if (this.value.discoveredUsFrom == null){
				if (this.discoveredUsFrom.isEmpty()){
					return false;
				}
				return true;
			}
			return JSON.stringify(this.value.discoveredUsFrom.getJSON()) != JSON.stringify(this.discoveredUsFrom.getJSON());
		},
		accountCreatedByItems():ISelectItem<string>[]{
			return [
				{
					text: "Winfakt Online",
					value: "accountCreatedByWinfaktOnline"
				},
				{
					text: "Winfakt Classic",
					value: "accountCreatedByWinfaktClassic"
				},
				{
					text: "Facturatie.be",
					value: "accountCreatedByFacturatieBe"
				}
			];
		},
		accountCreatedByValue():string[]{
			let result = [];
			if (this.value.accountCreatedByWinfaktOnline) {
				result.push("accountCreatedByWinfaktOnline");
			}
			if (this.value.accountCreatedByWinfaktClassic) {
				result.push("accountCreatedByWinfaktClassic");
			}
			if (this.value.accountCreatedByFacturatieBe) {
				result.push("accountCreatedByFacturatieBe");
			}
			return result;
		},
		paymentFrequencyItems():ISelectItem<PaymentFrequency>[]{
			return [
				{
					text: "Maandelijks",
					value: PaymentFrequency.Monthly
				},
				{
					text: "Kwartaal",
					value: PaymentFrequency.Quarter
				},
				{
					text: "Jaarlijks",
					value: PaymentFrequency.Yearly
				},
				{
					text: "Permanent",
					value: PaymentFrequency.Permanent
				}
			];
		},
		isAdmin():boolean {
			if (!AuthService.user) return false;
			return AuthService.user.hasPermission("admin");
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(){
				this.oldTier = this.value.tier;
				this.oldDemoStatus = this.value.isDemo;
				if (this.value.discoveredUsFrom != null){
					this.discoveredUsFrom = new DiscoveredUsFrom(this.value.discoveredUsFrom.getJSON());
				}else{
					this.discoveredUsFrom = new DiscoveredUsFrom();
				}
				this.didNotPurchaseReasons = this.value.DidNotPurchaseReasons.map(d=>d.ReasonID).filter(d=>d != DidNotPurchaseReason.MISC);
				this.didNotPurchaseReasonExplained = "";
				let foundReason = this.value.DidNotPurchaseReasons.find(d=>d.ReasonID == DidNotPurchaseReason.MISC);
				if (foundReason) {
					this.didNotPurchaseReasonExplained = foundReason.ExtraData;
				}
			}
		},
		discoveredUsFrom: {
			deep: true,
			async handler(newValue:DiscoveredUsFrom, oldValue:DiscoveredUsFrom){
				if (!this.isInfo) return;
				if (newValue != oldValue) return;
				await this.$nextTick();
				if (this.value.discoveredUsFrom){
					this.discoveredUsFrom = new DiscoveredUsFrom(this.value.discoveredUsFrom.getJSON());
				}else{
					this.discoveredUsFrom = new DiscoveredUsFrom();
				}
			}
		}
	},
	methods: {
		togglePermanentlyActive() {
			if (this.value.activeUntil.getTime() == 0) {
				this.value.activeUntil = new Date();
			} else {
				this.value.activeUntil = new Date(0);
			}
		},
		togglePermanentServicePack() {
			if (!this.value.servicePackExpirationDate) {
				this.value.servicePackExpirationDate = new Date();
			} else {
				this.value.servicePackExpirationDate = null;
			}
		},
		async saveCompany() {
			if (this.isInfo) return;
			this.value.DidNotPurchaseReasons = this.didNotPurchaseReasons.map(d=>{
				let reason = new CompanyDidNotPurchaseReasons();
				reason.ReasonID = d;
				return reason;
			});
			if (this.didNotPurchaseReasonExplained) {
				let reason = new CompanyDidNotPurchaseReasons();
				reason.ReasonID = DidNotPurchaseReason.MISC;
				reason.ExtraData = this.didNotPurchaseReasonExplained;
				this.value.DidNotPurchaseReasons.push(reason);
			}
			let company = await CompanyService.updateCompany(this.value);
			if (this.discoveredUsFromChanged){
				company.discoveredUsFrom = await CompanyService.setDiscoveredUsFrom(company, this.discoveredUsFrom);
				this.discoveredUsFrom = new DiscoveredUsFrom(company.discoveredUsFrom.getJSON());
			}
			if (this.oldTier != company.tier || this.oldDemoStatus != company.isDemo){
				let text = "De editie is gewijzigd, wil je de modules ook resetten naar de standaard modules voor deze editie?";
				let resetTier = await this.$wf.confirm(text, "Ja", "Neen", "Warning");
				if (resetTier){
					company = await CompanyService.resetModules(company);
				}
			}
			company = await CompanyService.getCompany(company.id);
			this.oldTier = company.tier;
			this.oldDemoStatus = company.isDemo;
			setTitle(company.name);
			this.$wf.notify("Company updated", "info", 3000, "info");
			this.$emit("input", company);
		},
		setAccountCreatedBy(value:string[]){
			this.value.accountCreatedByWinfaktOnline = value.indexOf("accountCreatedByWinfaktOnline") != -1;
			this.value.accountCreatedByWinfaktClassic = value.indexOf("accountCreatedByWinfaktClassic") != -1;
			this.value.accountCreatedByFacturatieBe = value.indexOf("accountCreatedByFacturatieBe") != -1;
		},
		async resetModules(){
			let resetTier = await this.$wf.confirm("Wil je de modules resetten naar de standaard modules voor deze editie?", "Ja", "Neen", "danger");
			if (resetTier){
				let company = await CompanyService.updateCompany(this.value);
				company = await CompanyService.resetModules(company);
				this.$emit("input", company);
			}
		},
		async moveToDb(dbId:number){
			if (!this.isAdmin) return;
			let message = `Weet je zeker dat je dit account wilt overzetten naar db${dbId + 1}?`;
			let confirm = await this.$wf.confirm(message, "Ja", "Nee", "Waarschuwing", "danger");
			if (!confirm) return;
			await CompanyService.moteToOtherDb(this.value, dbId);
			this.$wf.notify("Process is gestart", "info", 3000, "info-circle");
		},
		async onMoveToOtherDb(running:boolean){
			this.isBeingMoved = running;
		},
		async moveToDbQueued(dbId:number){
			if (!this.isAdmin) return;
			let message = `Weet je zeker dat je dit account wilt overzetten naar db${dbId + 1}?<br/>Deze actie word snachts uitgevoerd wanneer er een tijdslot voor is.`;
			let confirm = await this.$wf.confirm(message, "Ja", "Nee", "Waarschuwing", "danger");
			if (!confirm) return;
			await CompanyService.queueMoveToOtherDb(this.value, dbId);
		}
	},
	async mounted(){
		MainSocketService.on(`/company/${this.value.id}/move-to-other-db`, this.onMoveToOtherDb, this);
		this.isBeingMoved = await CompanyService.isMovingToAltDb(this.value);
		this.availableDbs = await MetricService.getAllDbIds();
	},
	destroyed(){
		MainSocketService.offAll(this);
	},
	components: {
		AppSelectTier,
		AppSelectDiscoveredUsFrom,
		AppSelectCompanyDidNotPurchaseReason,
		AppCompanyEditVars
	}
});
